import { FC, useEffect } from 'react';
import FirstLevelTable from './components/FirstLevelTable';
import HistoricalTable from '../../ucits/law/lawComponents/HistoricalTable';
import { useDispatch, useSelector } from 'react-redux';
import { Select } from '../../../../types/redux/pages/PagesStore';
import {
  addAdditionalTitleBarComponents,
  removeTitleBarComponent,
} from '../../../../redux/pages/actions';
import {
  APPEND_NEW_SELECT_TO_TITLEBAR,
  REMOVE_SELECT_FROM_TITLEBAR,
} from '../../../../types/redux/pages/pageTypes';
import {
  activePageSelector,
  activeSectionSelector,
} from '../../../../redux/pages/selectors';
import { FundInfoComponentProps } from '../../../layout/general/SimpleGeneralPageWrapper';
import { QueryBoundary } from '../../../utilityComponents/QueryBoundary';
import { useProspectusRestrictionsQuery } from '../../../../api/rs-hg/risk/hooks/queries';
import {
  extractProspectusRestrictionsPayload,
  type ProspectusRestrictionsPageModel,
} from '../../../../api/rs-hg/risk/prospectusRestrictions.types';
import useTitleBarSelect from '../../../../hooks/useTitleBarSelect';

const ProspectusRestrictions: FC<FundInfoComponentProps> = ({
  fundId,
  fundName,
  positionDate,
}) => {
  const dispatch = useDispatch();
  const activePage = useSelector(activePageSelector);
  const section = useSelector(activeSectionSelector) || '';
  const currentOrHistorical = useTitleBarSelect('dataDisplay');
  const isHistorical = currentOrHistorical === 'historical';
  const isPositionDateLoading = !!fundId && !positionDate;

  const prospectusRestrictionsQuery = useProspectusRestrictionsQuery(
    fundId,
    positionDate,
    isHistorical,
  );

  useEffect(() => {
    const selectsToAdd: Select[] = [
      {
        titleBarTitle: 'Data Display: ',
        titleBarKey: 'dataDisplay',
        values: [
          {
            label: 'Current',
            value: 'current',
          },
          {
            label: 'Historical',
            value: 'historical',
          },
        ],
        displayOnOverviewPage: false,
        aValueIsAlwaysSelected: true,
        currentValue: 'current',
      },
    ];

    dispatch(
      addAdditionalTitleBarComponents(
        selectsToAdd,
        APPEND_NEW_SELECT_TO_TITLEBAR,
      ),
    );

    return () => {
      dispatch(
        removeTitleBarComponent('dataDisplay', REMOVE_SELECT_FROM_TITLEBAR),
      );
    };
  }, [activePage, dispatch]);

  if (isHistorical) {
    return (
      <HistoricalTable
        fetchUrl="aggregate_monthly_prospectus_review_d"
        activeFund={fundId}
        reduxKey="prospectus-restrictions"
      />
    );
  }

  return (
    <QueryBoundary<ProspectusRestrictionsPageModel>
      isLoading={isPositionDateLoading || prospectusRestrictionsQuery.isLoading}
      isFetching={prospectusRestrictionsQuery.isFetching}
      isError={prospectusRestrictionsQuery.isError}
      error={prospectusRestrictionsQuery.error}
      data={
        extractProspectusRestrictionsPayload(prospectusRestrictionsQuery.data)
          ? prospectusRestrictionsQuery.data
          : null
      }
      loadingMessages={
        isPositionDateLoading
          ? ['Loading position date...']
          : ['Loading Prospectus Restrictions Data']
      }
      noDataMessage="No prospectus restrictions data available."
      errorMessage="Unable to load prospectus restrictions data."
    >
      {(pageModel) => (
        <FirstLevelTable
          fundDetails={{ id: fundId, name: fundName }}
          activeFund={fundId}
          pageModel={pageModel}
          isFetching={prospectusRestrictionsQuery.isFetching}
          activeFundName={fundName}
          section={section}
        />
      )}
    </QueryBoundary>
  );
};

export default ProspectusRestrictions;
